import { useEffect, useState } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { DollarSign, TrendingUp, Users, Loader2, Crown, AlertTriangle, Settings, BarChart3, Wallet } from 'lucide-react';

export default function RevenueBoard({ user }) {
  const [data, setData] = useState(null);
  const [commission, setCommission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [feeForm, setFeeForm] = useState({ dispatch_fee_percent: '', factoring_percent: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = () => {
    setLoading(true);
    Promise.all([
      api.get('/stats/owner-revenue'),
      api.get('/org/commission-settings')
    ])
      .then(([revRes, comRes]) => {
        setData(revRes.data);
        setCommission(comRes.data);
        setFeeForm({
          dispatch_fee_percent: String(comRes.data.dispatch_fee_percent),
          factoring_percent: String(comRes.data.factoring_percent)
        });
      })
      .catch(() => toast.error('Failed to load revenue data'))
      .finally(() => setLoading(false));
  };

  const saveSettings = () => {
    setSaving(true);
    api.put('/org/commission-settings', {
      dispatch_fee_percent: parseFloat(feeForm.dispatch_fee_percent),
      factoring_percent: parseFloat(feeForm.factoring_percent)
    })
      .then(() => {
        toast.success('Commission settings saved!');
        setShowSettings(false);
        loadData();
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to save'))
      .finally(() => setSaving(false));
  };

  if (user.role !== 'super_admin' && user.role !== 'broker') {
    return <p className="text-center text-slate-500 py-12">Broker or Super Admin access only.</p>;
  }

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!data) return <p className="text-center text-slate-500 py-12">No revenue data.</p>;

  const remaining = (commission?.dispatch_fee_percent || 10) - (commission?.factoring_percent || 4);

  return (
    <div className="space-y-6" data-testid="revenue-board-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Revenue Board</h1>
          <p className="text-slate-600 mt-1">Master revenue overview — all dispatchers, commissions, and splits</p>
        </div>
        <Button variant="outline" onClick={() => setShowSettings(!showSettings)} data-testid="commission-settings-btn">
          <Settings className="w-4 h-4 mr-2" />
          Commission Settings
        </Button>
      </div>

      {/* Commission Settings Panel */}
      {showSettings && (
        <Card className="border-blue-200 bg-blue-50/50" data-testid="commission-settings-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-600" />
              Commission / Split Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">Set the dispatch fee percentage and factoring company cut. The remaining amount after factoring splits 50/50 between Urbanvision and the agent.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Total Dispatch Fee %</Label>
                <Input
                  type="number" step="0.1" min="0" max="100"
                  value={feeForm.dispatch_fee_percent}
                  onChange={e => setFeeForm({ ...feeForm, dispatch_fee_percent: e.target.value })}
                  data-testid="dispatch-fee-input"
                />
                <p className="text-xs text-slate-500 mt-1">% charged on gross load amount</p>
              </div>
              <div>
                <Label>Factoring Company %</Label>
                <Input
                  type="number" step="0.1" min="0" max="100"
                  value={feeForm.factoring_percent}
                  onChange={e => setFeeForm({ ...feeForm, factoring_percent: e.target.value })}
                  data-testid="factoring-pct-input"
                />
                <p className="text-xs text-slate-500 mt-1">Factoring company's cut (max 4% recommended)</p>
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 text-sm space-y-1 border">
              <p className="font-medium">Preview on $2,000 load:</p>
              <p>Total fee: ${(2000 * parseFloat(feeForm.dispatch_fee_percent || 0) / 100).toFixed(2)}</p>
              <p className="text-red-600">Factoring: -${(2000 * parseFloat(feeForm.factoring_percent || 0) / 100).toFixed(2)}</p>
              <p className="text-blue-600">Urbanvision: ${((2000 * (parseFloat(feeForm.dispatch_fee_percent || 0) - parseFloat(feeForm.factoring_percent || 0)) / 100) / 2).toFixed(2)}</p>
              <p className="text-green-600">Agent: ${((2000 * (parseFloat(feeForm.dispatch_fee_percent || 0) - parseFloat(feeForm.factoring_percent || 0)) / 100) / 2).toFixed(2)}</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={saveSettings} disabled={saving} data-testid="save-commission-btn">
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
              <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Time Period Revenue */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-blue-500" data-testid="revenue-today">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-medium text-slate-500 uppercase">Today</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">${data.time_periods.today.revenue.toLocaleString()}</p>
            <p className="text-xs text-blue-600">{data.time_periods.today.loads} loads</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-indigo-500" data-testid="revenue-week">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-medium text-slate-500 uppercase">This Week</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">${data.time_periods.week.revenue.toLocaleString()}</p>
            <p className="text-xs text-indigo-600">{data.time_periods.week.loads} loads</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500" data-testid="revenue-month">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-medium text-slate-500 uppercase">This Month</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">${data.time_periods.month.revenue.toLocaleString()}</p>
            <p className="text-xs text-purple-600">{data.time_periods.month.loads} loads</p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Split Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card data-testid="total-revenue">
          <CardContent className="pt-4 pb-3 text-center">
            <DollarSign className="w-6 h-6 mx-auto text-slate-600 mb-1" />
            <p className="text-2xl font-bold">${data.totals.gross_revenue.toLocaleString()}</p>
            <p className="text-xs text-slate-500">Total Revenue</p>
          </CardContent>
        </Card>
        <Card data-testid="total-loads">
          <CardContent className="pt-4 pb-3 text-center">
            <TrendingUp className="w-6 h-6 mx-auto text-blue-600 mb-1" />
            <p className="text-2xl font-bold">{data.totals.total_loads}</p>
            <p className="text-xs text-slate-500">Total Loads</p>
          </CardContent>
        </Card>
        <Card data-testid="company-share">
          <CardContent className="pt-4 pb-3 text-center">
            <Wallet className="w-6 h-6 mx-auto text-blue-600 mb-1" />
            <p className="text-2xl font-bold text-blue-600">${data.totals.company_share.toLocaleString()}</p>
            <p className="text-xs text-slate-500">Company Share</p>
          </CardContent>
        </Card>
        <Card data-testid="agent-commissions">
          <CardContent className="pt-4 pb-3 text-center">
            <Users className="w-6 h-6 mx-auto text-green-600 mb-1" />
            <p className="text-2xl font-bold text-green-600">${data.totals.agent_commissions.toLocaleString()}</p>
            <p className="text-xs text-slate-500">Agent Commissions</p>
          </CardContent>
        </Card>
        <Card data-testid="factoring-total">
          <CardContent className="pt-4 pb-3 text-center">
            <DollarSign className="w-6 h-6 mx-auto text-red-600 mb-1" />
            <p className="text-2xl font-bold text-red-600">${data.totals.factoring_total.toLocaleString()}</p>
            <p className="text-xs text-slate-500">Factoring Fees</p>
          </CardContent>
        </Card>
      </div>

      {/* Best / Worst Performer */}
      {(data.best_performer || data.worst_performer) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.best_performer && (
            <Card className="border-green-200 bg-green-50/30" data-testid="best-performer">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-3">
                  <Crown className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="text-xs text-green-600 uppercase font-medium">Best Performer</p>
                    <p className="text-lg font-bold text-slate-900">{data.best_performer.name} <span className="text-sm text-blue-600">(PIN: {data.best_performer.pin})</span></p>
                    <p className="text-sm text-slate-600">{data.best_performer.total_loads} loads | ${data.best_performer.gross_revenue.toLocaleString()} revenue</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {data.worst_performer && (
            <Card className="border-amber-200 bg-amber-50/30" data-testid="worst-performer">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-8 h-8 text-amber-600" />
                  <div>
                    <p className="text-xs text-amber-600 uppercase font-medium">Needs Improvement</p>
                    <p className="text-lg font-bold text-slate-900">{data.worst_performer.name} <span className="text-sm text-blue-600">(PIN: {data.worst_performer.pin})</span></p>
                    <p className="text-sm text-slate-600">{data.worst_performer.total_loads} loads | ${data.worst_performer.gross_revenue.toLocaleString()} revenue</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Dispatcher Breakdown Table */}
      <Card data-testid="dispatcher-breakdown">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Breakdown by Dispatcher ({data.dispatcher_breakdown.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.dispatcher_breakdown.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No dispatcher data yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="dispatcher-table">
                <thead>
                  <tr className="border-b text-left text-xs text-slate-500 uppercase">
                    <th className="pb-2 pr-3">Dispatcher</th>
                    <th className="pb-2 pr-3">PIN</th>
                    <th className="pb-2 pr-3">Loads</th>
                    <th className="pb-2 pr-3">Gross Revenue</th>
                    <th className="pb-2 pr-3">Total Fee</th>
                    <th className="pb-2 pr-3 text-red-600">Factoring</th>
                    <th className="pb-2 pr-3 text-blue-600">Company</th>
                    <th className="pb-2 pr-3 text-green-600">Agent</th>
                    <th className="pb-2">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {data.dispatcher_breakdown.map((d, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`dispatcher-row-${i}`}>
                      <td className="py-2 pr-3 font-medium">{d.name}</td>
                      <td className="py-2 pr-3 text-blue-600 font-mono">{d.pin}</td>
                      <td className="py-2 pr-3">{d.total_loads}</td>
                      <td className="py-2 pr-3 font-bold">${d.gross_revenue.toLocaleString()}</td>
                      <td className="py-2 pr-3">${d.total_fee.toLocaleString()}</td>
                      <td className="py-2 pr-3 text-red-600">${d.factoring_total.toLocaleString()}</td>
                      <td className="py-2 pr-3 text-blue-600 font-bold">${d.company_share.toLocaleString()}</td>
                      <td className="py-2 pr-3 text-green-600 font-bold">${d.agent_share.toLocaleString()}</td>
                      <td className="py-2">{d.active_loads}</td>
                    </tr>
                  ))}
                  {/* Totals Row */}
                  <tr className="border-t-2 border-slate-300 font-bold bg-slate-50">
                    <td className="py-2 pr-3" colSpan={2}>TOTALS</td>
                    <td className="py-2 pr-3">{data.totals.total_loads}</td>
                    <td className="py-2 pr-3">${data.totals.gross_revenue.toLocaleString()}</td>
                    <td className="py-2 pr-3">${(data.totals.company_share + data.totals.agent_commissions + data.totals.factoring_total).toLocaleString()}</td>
                    <td className="py-2 pr-3 text-red-600">${data.totals.factoring_total.toLocaleString()}</td>
                    <td className="py-2 pr-3 text-blue-600">${data.totals.company_share.toLocaleString()}</td>
                    <td className="py-2 pr-3 text-green-600">${data.totals.agent_commissions.toLocaleString()}</td>
                    <td className="py-2"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
